import { useQuery, useLazyQuery, useMutation } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_PROGRAMA_KETO_MEDICO, { adaptAddVisitaKetoMedicoInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProgramaKetoMedico";
import { useStepSave } from "../../../../hooks/useStepSave";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { getStringQueryCombosRI } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI";
import { adaptCombosRIArray } from "../../../../../../adapters/resumenIngreso/combosRIAdapter";
import { handleChangeImageVisita } from "../../../../utils/handleChangeImageVisita";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import { IndicacionesVisitaInput } from "./components/IndicacionesVisitaInput";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { GET_SOLICITUD_INDICACION_PENDIENTE } from "../../../../../../data/graphQL/queries/GestionAsistencial/getSolicitudIndicacionPendiente";

export default function ProgramaKetoMedico({ readOnly, onSaveRef, onExposeFormatted, complicacionesCombo, esUrgencia }) {
  const [formData, setFormData] = useState({});
  const [combosRI, setCombosRI] = useState();
  const [solicitudIndicacionPendiente, setSolicitudIndicacionPendiente] = useState(null);
  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const { deleteArchivo } = useDeleteArchivoVisita();
  const [cambioIndicacion, setCambioIndicacion] = useState(false);

  const inputCombos = groups.flatMap((g) => g.inputs.filter((i) => !!i.comboName));
  const { data: dataCombosRI } = useQuery(getStringQueryCombosRI(inputCombos), {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!dataCombosRI) return;
    setCombosRI(adaptCombosRIArray(dataCombosRI))
  }, [dataCombosRI]);

  const [getSolicitudIndicacionPendiente, { data: dataSolicitudPendiente }] = useLazyQuery(GET_SOLICITUD_INDICACION_PENDIENTE, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!dataSolicitudPendiente) return;
    setSolicitudIndicacionPendiente(dataSolicitudPendiente.solicitudIndicacionPendiente.success && dataSolicitudPendiente.solicitudIndicacionPendiente.valorEsperado != null);
  }, [dataSolicitudPendiente]);

  const [save] = useMutation(SAVE_PROGRAMA_KETO_MEDICO);

  useEffect(() => {
    const visitaIndicaciones = visita?.listaIndicacion || [];
    const formIndicaciones = formData?.listaIndicacion || [];

    if (visitaIndicaciones.length !== formIndicaciones.length) {
      setCambioIndicacion(true);
      return;
    }

    const formMap = new Map(
      formIndicaciones.map(ind => [ind.idInsumo, ind.cantidadDiaria])
    );

    const hayCambios = visitaIndicaciones.some(visitaInd => {
      const formCantidad = formMap.get(visitaInd.idInsumo);
      // Si no existe el insumo o la cantidad es diferente
      return formCantidad === undefined || formCantidad !== visitaInd.cantidadDiaria;
    });

    setCambioIndicacion(hayCambios);
  }, [visita?.listaIndicacion, formData?.listaIndicacion]);

  const formattedGroups = useMemo(() => {
    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };
        if (newInput.comboName) newInput.options = combosRI?.[input.name];

        if (input.name === "justificacionModificacion") {
          newInput.required = cambioIndicacion;
        }

        if (input.name === "complicaciones") {
          const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === 'ninguna')?.id;
          newInput.options = complicacionesCombo?.map((opt) => {
            if (Array.isArray(formData.complicaciones)) {
              const hasNinguna = formData.complicaciones.includes(ningunaId);
              const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
              if (hasNinguna) {
                // Si "ninguna" está seleccionada, deshabilitar todas menos "ninguna"
                return { ...opt, disabled: opt.id !== ningunaId };
              }
              if (hasOther) {
                // Si hay alguna distinta de "ninguna", deshabilitar "ninguna"
                return { ...opt, disabled: opt.id === ningunaId };
              }
            }
            return { ...opt, disabled: false };
          });
        }

        if (readOnly) newInput.disabled = true;

        return newInput;
      });
      const hidden = !formattedInputs.some((i) => !i.hidden);
      return { ...group, hidden, inputs: formattedInputs };
    });
  }, [readOnly, combosRI, complicacionesCombo, formData?.complicaciones, cambioIndicacion]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeFile = useCallback((name, fileValue) => {
    handleChangeImageVisita({
      saveArchivoVisita,
      name,
      fileValue,
      idVisita: visita.idVisita,
      handleChange,
      idTipoArchivo: tipoArchivoVisita.LABORATORIO
    });
  }, [visita.idVisita, saveArchivoVisita]);

  const onDeleteImage = (name) => {
    const file = formData[name];
    deleteArchivo({ idArchivo: file.idArchivo, name, handleChange });
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });

    const { data } = await save({ variables: { input: adaptAddVisitaKetoMedicoInput(formData) } });

    const idVisitaKeto = data?.saveVisitaKetoMedico?.idVisitaKeto;
    if (!idVisitaKeto) throw new Error("Error al guardar");

    setVisita((prev) => ({ ...prev, ...formData, idVisitaKeto }));
  });

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(groups, true);

      setFormData({
        ...filterVisitaData(visita, inputNames),
        idVisitaKeto: visita.idVisitaKeto,
        idVisita: visita.idVisita
      });
    }
  }, [visita]);

  useEffect(() => {
    if (!visita?.idEvaluacion) return;
    
    getSolicitudIndicacionPendiente({ variables: { idEvaluacion: visita?.idEvaluacion } });
  }, [visita?.idEvaluacion]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaKetoMedico",
      stepName: "Programa Ketosteril",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
      {formattedGroups.
        filter((group) => !group.hidden).
        map((group, idx) =>
          <CardBox key={idx} title={group.title}>
            <Grid container spacing={2}>
              {group.inputs.
                filter((input) => !input.hidden).
                map((input) =>
                  <Grid size={{ xs: 12 }} key={input.name}>
                    {input.name !== "listaIndicacion" ?
                      <RenderInputByTypeMobile
                        input={input}
                        value={formData[input.name]}
                        handleChange={input.type === "file" ? handleChangeFile : handleChange}
                        handleDelete={input.type === "file" ? onDeleteImage : null}
                        row={input.type === "multiple_boolean"} /> :


                      <IndicacionesVisitaInput
                        readOnly={readOnly}
                        handleChange={handleChange}
                        indicaciones={formData[input.name]}
                        idEvaluacion={visita?.idEvaluacion}
                        inputName={input.name}
                        solicitudIndicacionPendiente={solicitudIndicacionPendiente}
                      />

                    }
                  </Grid>
                )}
            </Grid>
          </CardBox>
        )}
    </>);

}

const groups = [
  {
    title: "",
    inputs: [
      {
        name: "idComboRIValorEstadio",
        displayName: "ERC Estadio",
        comboName: "ERC estadio",
        type: "select",
        required: true
      },
      {
        name: "fG",
        displayName: "FG (MDRD / CKD-EPI)",
        type: "float",
        required: true
      }]

  },
  {
    title: "Indicaciones",
    inputs: [
      {
        name: "listaIndicacion",
        displayName: "Indicaciones",
        type: "custom",
        required: true
      },
      {
        name: "justificacionModificacion",
        displayName: "Justificación por modificación",
        type: "long_string",
        maxLength: 4000
      },
      {
        name: "indicacionComentario",
        displayName: "Comentarios de indicación",
        type: "long_string",
        maxLength: 4000
      }]

  },
  {
    title: "Requerimientos",
    inputs: [
      {
        name: "laboratorioAdjunto",
        displayName: "Laboratorio (adjuntar)",
        type: "file"
      },
      {
        name: "complicaciones",
        displayName: "Complicaciones",
        type: "multiple_select",
        required: true
      }]

  }];